/**
 * Environment slice — the tide cycle and wind for the current scenario, plus
 * the scenario clock (hours since t=0). The clock is advanced by the
 * simulation engine; until the sim is running, it sits where the scenario
 * pinned it (default 0).
 */
import { create } from 'zustand';
import { DEFAULT_TIDE, type TideCycle } from '@/environment';
import { VICTORIA_DEFAULT_MS, VICTORIA_WINDOW, type StreamSourceId } from '@/tides';
import { WEATHER_SOURCES, type WeatherSourceId } from '@/weather';
import { loadJson, saveJson } from '@/store/persist';

/** Which overlay the chart shows in Plotting: tidal streams or weather. */
export type OverlayMode = 'streams' | 'weather';

interface EnvState {
  tide: TideCycle;
  setTide: (patch: Partial<TideCycle>) => void;
  /** Wind direction the wind comes FROM, degrees true. */
  windDirDeg: number;
  /** Wind speed in knots. */
  windSpeedKn: number;
  setWind: (dir: number, kn: number) => void;
  /** Scenario clock — hours since t=0. */
  clockH: number;
  setClockH: (h: number) => void;
  advanceClockH: (deltaH: number) => void;
  /**
   * The instant (UTC ms) the tidal-stream overlay is showing. The scrubber moves
   * this through the HW-Victoria window; hours-from-HW and the spring/neap state
   * are DERIVED from it via the Victoria tide table (`tidalStateAt`).
   */
  streamTimeMs: number;
  setStreamTimeMs: (ms: number) => void;
  /** Which tidal-stream source dataset is displayed (one at a time). */
  /** Whether the summonable tidal-stream scrubber bar is open. Closed = arrows at now, no bar. */
  scrubberOpen: boolean;
  openScrubber: () => void;
  /** Close the scrubber and snap the stream time back to now. */
  closeScrubber: () => void;
  streamSource: StreamSourceId;
  setStreamSource: (s: StreamSourceId) => void;

  /**
   * Which overlay the chart shows in Plotting: tidal streams or weather. They
   * swap so we can flick between them while staying on the chart.
   */
  overlayMode: OverlayMode;
  setOverlayMode: (m: OverlayMode) => void;
  /** Which weather source is displayed (one at a time). */
  weatherSource: WeatherSourceId;
  setWeatherSource: (s: WeatherSourceId) => void;
  /** Forecast offset for REAL weather, hours from now (0 = current). */
  weatherForecastH: number;
  setWeatherForecastH: (h: number) => void;

  /** The reference port for the tidal-height card (and bridge clearance-now). */
  heightPortId: string;
  setHeightPortId: (id: string) => void;

  /**
   * A wind barb the skipper pinned to a tapped spot via the "Wind here" ring
   * action. Shown on the chart until they tap elsewhere. null = nothing pinned.
   */
  pinnedWind: { position: [number, number]; dirFromDeg: number; speedKn: number } | null;
  setPinnedWind: (w: { position: [number, number]; dirFromDeg: number; speedKn: number }) => void;
  clearPinnedWind: () => void;
}

const WEATHER_SOURCE_LS = 'acp.weatherSource.v1';

/**
 * Which weather source to open on.
 *
 * It used to reset to the synthetic training wind on every launch, which was
 * merely annoying until a renderer death started ending the process: a skipper on
 * real water was being put back on made-up wind by something they never asked
 * for and might not notice, since a synthetic barb field looks exactly like a
 * real one. So it persists, the same way the chart provider, the tide sources and
 * the position source already do.
 *
 * A saved real model is only honoured on a chart of real water. Coming back onto
 * the fictional training charts with GFS selected would ask Open-Meteo for a
 * forecast for a place that does not exist, so the caller (ConditionsSheet) keeps
 * that gate; what is stored here is the skipper's choice, not permission to use
 * it.
 */
/**
 * Phones that saved a source under the old three-letter names, before the app
 * settled on one set of Open-Meteo model ids. Without this, anyone who had picked
 * a real model comes back on the synthetic training wind and is not told why.
 */
const LEGACY_SOURCE_IDS: Record<string, WeatherSourceId> = {
  gfs: 'gfs_seamless',
  ecmwf: 'ecmwf_ifs',
  icon: 'icon_seamless',
};

function loadWeatherSource(): WeatherSourceId {
  const saved = loadJson<string>(WEATHER_SOURCE_LS, 'synthetic');
  const migrated = LEGACY_SOURCE_IDS[saved] ?? saved;
  return WEATHER_SOURCES.some((w) => w.id === migrated)
    ? (migrated as WeatherSourceId)
    : 'synthetic';
}

export const useEnvStore = create<EnvState>((set) => ({
  tide: DEFAULT_TIDE,
  setTide: (patch) => set((s) => ({ tide: { ...s.tide, ...patch } })),
  windDirDeg: 270,
  windSpeedKn: 10,
  setWind: (windDirDeg, windSpeedKn) => set({ windDirDeg, windSpeedKn }),
  pinnedWind: null,
  setPinnedWind: (pinnedWind) => set({ pinnedWind }),
  clearPinnedWind: () => set({ pinnedWind: null }),
  clockH: 0,
  setClockH: (clockH) => set({ clockH }),
  advanceClockH: (deltaH) => set((s) => ({ clockH: s.clockH + deltaH })),
  streamTimeMs: VICTORIA_DEFAULT_MS,
  setStreamTimeMs: (ms) =>
    set({ streamTimeMs: Math.max(VICTORIA_WINDOW.startMs, Math.min(VICTORIA_WINDOW.endMs, ms)) }),
  scrubberOpen: false,
  openScrubber: () => set({ scrubberOpen: true }),
  closeScrubber: () => set({ scrubberOpen: false, streamTimeMs: VICTORIA_DEFAULT_MS }),
  streamSource: 'chart',
  setStreamSource: (streamSource) => set({ streamSource }),
  overlayMode: 'streams',
  setOverlayMode: (overlayMode) => set({ overlayMode }),
  weatherSource: loadWeatherSource(),
  setWeatherSource: (weatherSource) => {
    saveJson(WEATHER_SOURCE_LS, weatherSource);
    set({ weatherSource });
  },
  weatherForecastH: 0,
  setWeatherForecastH: (weatherForecastH) => set({ weatherForecastH }),
  heightPortId: 'victoria',
  setHeightPortId: (heightPortId) => set({ heightPortId }),
}));
